import { ReactNode, useEffect, useState } from 'react'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react'
import { InteractionStatus, BrowserAuthError } from '@azure/msal-browser'
import { accountPickerRequest } from '../config/authConfig'
import { useRoles } from '../hooks/useRoles'
import { useActiveAccount } from '../hooks/useActiveAccount'
import { Spinner, SpinnerSize, PrimaryButton, Stack, Text, MessageBar, MessageBarType } from '@fluentui/react'

interface ProtectedRouteProps {
  children: ReactNode
}

function clearMsalInteractionState() {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.includes('msal.interaction') || key.includes('msal.request'))) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key))
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { instance, inProgress, accounts } = useMsal()
  const { hasAnyRole } = useRoles()
  const [error, setError] = useState<string | null>(null)
  const [hasAutoRedirected, setHasAutoRedirected] = useState(false)

  const triggerLogin = async () => {
    if (inProgress !== InteractionStatus.None) return
    setError(null)
    try {
      await instance.loginRedirect(accountPickerRequest)
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
        clearMsalInteractionState()
        setTimeout(async () => {
          try {
            await instance.loginRedirect(accountPickerRequest)
          } catch {
            setError('Login failed. Please refresh the page and try again.')
          }
        }, 500)
      } else {
        setError('Login failed. Please try again.')
        console.error('Login error:', err)
      }
    }
  }

  // Auto-trigger the Microsoft account picker once on initial unauthenticated load.
  // This makes the portal behave like Microsoft sites: visit → silent SSO tried in
  // main.tsx → if that didn't sign you in, you land directly on the account picker
  // rather than a page with a "Sign in" button.
  useEffect(() => {
    if (
      accounts.length === 0 &&
      inProgress === InteractionStatus.None &&
      !hasAutoRedirected &&
      !error
    ) {
      setHasAutoRedirected(true)
      triggerLogin()
    }
  }, [accounts.length, inProgress, hasAutoRedirected, error])

  if (inProgress !== InteractionStatus.None) {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        styles={{ root: { minHeight: '100vh' } }}
      >
        <Spinner size={SpinnerSize.large} label="Signing in..." />
      </Stack>
    )
  }

  return (
    <>
      <AuthenticatedTemplate>
        {hasAnyRole ? (
          children
        ) : (
          <NoRoleAssigned onSwitchAccount={() => instance.loginRedirect(accountPickerRequest)} />
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          tokens={{ childrenGap: 24 }}
          styles={{
            root: {
              minHeight: '100vh',
              backgroundColor: '#1a1a2e',
              color: '#ffffff',
            },
          }}
        >
          <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="xxLarge" styles={{ root: { color: '#ffffff', fontWeight: 600 } }}>
              SSO Admin Portal
            </Text>
            <Text variant="large" styles={{ root: { color: 'rgba(255,255,255,0.7)' } }}>
              Manage your application credentials
            </Text>
          </Stack>

          {error && (
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              styles={{ root: { maxWidth: 400 } }}
            >
              {error}
            </MessageBar>
          )}

          <PrimaryButton
            text="Sign in with Microsoft"
            onClick={triggerLogin}
            disabled={inProgress !== InteractionStatus.None}
            styles={{ root: { padding: '12px 32px', height: 'auto', fontSize: '16px' } }}
          />

          <Text variant="small" styles={{ root: { color: 'rgba(255,255,255,0.5)' } }}>
            Use your organizational account to sign in
          </Text>
        </Stack>
      </UnauthenticatedTemplate>
    </>
  )
}

function NoRoleAssigned({ onSwitchAccount }: { onSwitchAccount: () => void }) {
  const account = useActiveAccount()
  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      tokens={{ childrenGap: 16 }}
      styles={{ root: { minHeight: '100vh', padding: 24 } }}
    >
      <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
        Access pending
      </Text>
      <Text variant="medium" styles={{ root: { maxWidth: 480, textAlign: 'center' } }}>
        You're signed in as <strong>{account?.username}</strong> but you don't have a role
        assigned for this application yet. Ask an administrator to grant you the
        Administrator or Viewer role in Entra ID.
      </Text>
      <PrimaryButton text="Sign in with a different account" onClick={onSwitchAccount} />
    </Stack>
  )
}
